import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import Icon from "@/components/icon/Icon";
import { FoldingArrowDown, Sizes } from "@/components/icon/IconList";
import { mixpanelEvent } from "@/lib/mixpanels/mixpanel";
import { cn } from "@/lib/utils";
import useWindowDimensions from "@/lib/web-ui/useWindowDimensions";
import { Button, Divider, Icon as IconSds, TabContent, Tabs, Typography } from "@bookips/sds";
import { useRouter } from "next/router";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import SubGNBPromotion from "./components/SubGNBPromotion";
import { useActiveMarketingTab, useDropdownPosition } from "./hooks";
const SubjectTabs = /*#__PURE__*/ memo((param)=>{
    let { tabs , value , size , onValueChange , hideIndicator  } = param;
    const classes = useMemo(()=>({
            trigger: "h-[48px] hover:!text-gray-900 transition-colors selected:!text-gray-900",
            // 기본 회색 선 숨김
            list: "[&>div>div:first-child]:hidden",
            listContent: hideIndicator ? "[&>div:last-child]:opacity-0 [&>div:last-child]:!transition-none" : ""
        }), [
        hideIndicator
    ]);
    return /*#__PURE__*/ _jsx(Tabs, {
        size: size,
        value: value,
        tabs: tabs,
        onValueChange: onValueChange,
        classes: classes,
        className: "shrink-0",
        children: tabs.map((tab)=>/*#__PURE__*/ _jsx(TabContent, {
                value: tab.value,
                className: "hidden"
            }, tab.value))
    });
});
SubjectTabs.displayName = "SubjectTabs";
export const GNBSubNav = /*#__PURE__*/ memo((param)=>{
    let { selectedHomeId , onHomeChange , homeNavigation , marketingTabs , onCategoryHover , isCategoryMenuOpen  } = param;
    const { isMobile , isTablet  } = useWindowDimensions();
    // 모바일에서 열린 메뉴 ID 관리 (한 번에 하나만 열림)
    const [openMenuId, setOpenMenuId] = useState(null);
    // URL 경로 기반 마케팅 탭 활성화 상태
    const { activeTabId , isMarketingTabActive  } = useActiveMarketingTab(marketingTabs);
    const router = useRouter();
    // 홈 경로 여부 — 홈/카테고리/마케팅 탭 경로가 아니면 모든 탭 비선택 상태로 표시
    const isHomePage = router.pathname === "/";
    // /categories 경로일 때 카테고리 버튼 활성화 (마케팅 탭 활성화 시 제외)
    const isCategoryPage = router.pathname.startsWith("/categories");
    const isCategoryActive = (isCategoryMenuOpen || isCategoryPage) && !isMarketingTabActive;
    // 마케팅 탭 활성화 시 과목 탭 선택 초기화
    // prevMarketingActiveRef를 사용하여 마케팅 페이지로 전환되는 순간만 감지
    const prevMarketingActiveRef = useRef(isMarketingTabActive);
    useEffect(()=>{
        // 마케팅 탭이 비활성 → 활성으로 전환되는 순간만 selectedHomeId 초기화
        if (isMarketingTabActive && !prevMarketingActiveRef.current && selectedHomeId) {
            onHomeChange("");
        }
        prevMarketingActiveRef.current = isMarketingTabActive;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isMarketingTabActive
    ]);
    // 탭 영역 클릭 시 홈이 아니면 홈으로 이동하고, 스크롤을 맨 위로 올림
    const handleTabAreaClick = ()=>{
        if (router.asPath !== "/") {
            router.push("/");
        }
        window.scrollTo(0, 0);
    };
    // sds의 Tabs의 경우, 이미 선택된 탭을 클릭하면 이벤트가 발생하지 않습니다.
    // 하여, 위의 handleTabAreaClick 핸들러에서 홈으로 이동하는 것으로 대체합니다.
    // 하지만 homeId를 변경해야하기 때문에 역할을 분리해서 handleTabAreaClick 핸들러를 사용합니다.
    // 더 좋은 방안이 있다면 공유 부탁드립니다. 해당 주석은 2026-02-03 @summer 작성되었습니다.
    const handleChangeTab = useCallback((value)=>{
        onHomeChange(value);
        // URL에 homeId 쿼리가 있으면 제거 (탭 클릭이 queryHomeId에 덮어쓰이지 않도록)
        if (router.query.homeId) {
            const { homeId: _ , ...restQuery } = router.query;
            router.push({
                pathname: "/",
                query: restQuery
            }, undefined, {
                shallow: true
            });
        }
        mixpanelEvent("Tab UI Clicked", {
            "Tab Type": "Subject Home",
            "Tab Name": value
        });
    }, [
        onHomeChange,
        router
    ]);
    // homeNavigation을 Tabs 컴포넌트 형식으로 변환
    const tabs = useMemo(()=>homeNavigation.map((item)=>({
                label: item.homeName,
                value: item.homeId
            })), [
        homeNavigation
    ]);
    // selectedHomeId가 실제 탭 목록에 있는지 확인 (비공개 homeId 대응)
    const isValidHomeId = tabs.some((tab)=>tab.value === selectedHomeId);
    // 홈 경로가 아니거나, 카테고리/마케팅 탭이 활성화되었거나, 홈이 선택되지 않았거나, 유효하지 않은 homeId일 때 indicator 숨김
    const hideIndicator = !isHomePage || isCategoryActive || isMarketingTabActive || !selectedHomeId || !isValidHomeId;
    return /*#__PURE__*/ _jsx("section", {
        className: "ml-[calc(50%-50vw)] flex w-screen items-center justify-center border-b border-gray-100 bg-white px-6 max-lg:px-0",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "scrollbar-hide flex w-full max-w-[1200px] items-center justify-between overflow-x-scroll",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center gap-4 max-lg:pl-8 max-sm:gap-2.5 max-sm:pl-5",
                    children: [
                        /*#__PURE__*/ _jsxs("button", {
                            type: "button",
                            className: cn("group hidden h-[48px] shrink-0 !cursor-pointer items-center gap-1.5 whitespace-nowrap lg:flex", isCategoryActive ? "border-b-2 border-gray-900" : "border-b-2 border-transparent hover:border-gray-900"),
                            onMouseEnter: onCategoryHover,
                            children: [
                                /*#__PURE__*/ _jsx(IconSds.Category_2px, {
                                    className: cn("size-5 transition-colors", isCategoryActive ? "text-gray-900" : "text-gray-500 group-hover:text-gray-900")
                                }),
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: "h6",
                                    className: cn("transition-colors", isCategoryActive ? "text-gray-900" : "text-gray-500 group-hover:text-gray-900"),
                                    children: "카테고리"
                                })
                            ]
                        }),
                        tabs.length > 0 && /*#__PURE__*/ _jsx("div", {
                            onClick: handleTabAreaClick,
                            children: /*#__PURE__*/ _jsx(SubjectTabs, {
                                tabs: tabs,
                                value: !isHomePage || isCategoryActive || isMarketingTabActive || !selectedHomeId || !isValidHomeId ? "" : selectedHomeId,
                                size: isMobile ? "md" : "lg",
                                onValueChange: handleChangeTab,
                                hideIndicator: hideIndicator
                            })
                        }),
                        marketingTabs.length > 0 && /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(Divider, {
                                    orientation: "vertical",
                                    className: "h-3.5 shrink-0 self-center bg-gray-900/20"
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "flex h-[48px] items-center gap-4 max-lg:pr-8 max-sm:gap-2.5 max-sm:pr-5",
                                    children: marketingTabs.map((tab)=>/*#__PURE__*/ _jsx(AdMenu, {
                                            id: tab.id,
                                            title: tab.tabLabel,
                                            tabUrl: tab.tabUrl,
                                            items: tab.items,
                                            isMobile: isMobile,
                                            isTablet: isTablet,
                                            isOpen: openMenuId === tab.id,
                                            onOpenChange: (id)=>setOpenMenuId(openMenuId === id ? null : id),
                                            isActive: activeTabId === tab.id
                                        }, tab.id))
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "flex shrink-0 items-center max-lg:pr-8 max-sm:hidden",
                    children: /*#__PURE__*/ _jsx(SubGNBPromotion, {})
                })
            ]
        })
    });
});
GNBSubNav.displayName = "GNBSubNav";
const AD_MENU_DROPDOWN_WIDTH = 133;
const AdMenu = (param)=>{
    let { id , title , tabUrl , items , isMobile , isTablet , isOpen , onOpenChange , isActive  } = param;
    const buttonRef = useRef(null);
    const router = useRouter();
    const { position: dropdownPosition , updatePosition  } = useDropdownPosition({
        dropdownWidth: AD_MENU_DROPDOWN_WIDTH
    });
    // items가 있으면 드롭다운, 없으면 단순 링크
    const isDropdown = items && items.length > 0;
    // PC에서만 hover로 열기 (태블릿/모바일은 오버레이가 버튼을 덮어 hover 시 깜빡임 발생하므로 제외)
    const handlePointerEnter = (e)=>{
        if (isMobile || isTablet) {
            return;
        }
        if (e.pointerType === "mouse" && isDropdown && !isOpen) {
            updatePosition(buttonRef.current);
            onOpenChange(id);
        }
    };
    // PC에서만 hover로 닫기
    const handlePointerLeave = (e)=>{
        if (isMobile || isTablet) return;
        if (e.pointerType === "mouse" && isDropdown && isOpen) {
            onOpenChange(id);
        }
    };
    const handleClick = ()=>{
        // 모든 디바이스 공통: 닫혀있으면 클릭으로 열기 (PC는 hover로 이미 열려있을 수 있어 무시)
        // 닫기는 오버레이 클릭으로 처리 (열린 상태에서 버튼은 오버레이에 가려짐)
        // 모바일을 pointerup이 아닌 click으로 열어야, 같은 탭의 뒤따르는 click이
        // 새로 렌더된 오버레이에 떨어져 즉시 닫히는 깜빡임 현상을 방지할 수 있음
        if (isDropdown && !isOpen) {
            updatePosition(buttonRef.current);
            onOpenChange(id);
        } else if (!isDropdown && tabUrl) {
            router.push(tabUrl);
        }
        mixpanelEvent("Tab UI Clicked", {
            "Tab Type": "Subject Home",
            "Tab Name": title
        });
    };
    return /*#__PURE__*/ _jsxs("div", {
        ref: buttonRef,
        className: cn("group relative flex h-full shrink-0 items-center", isActive ? "border-b-2 border-gray-900 text-gray-900" : ""),
        onPointerEnter: handlePointerEnter,
        onPointerLeave: handlePointerLeave,
        children: [
            /*#__PURE__*/ _jsx(Button, {
                variant: "text",
                size: "sm",
                color: "danger",
                onClick: handleClick,
                rightElement: isDropdown && /*#__PURE__*/ _jsx(Icon, {
                    icon: FoldingArrowDown,
                    size: Sizes.small,
                    className: cn("size-3 rotate-180 transition-transform group-hover:text-gray-900", isOpen && "rotate-0", isOpen || isActive ? "text-gray-900" : "text-gray-400")
                }),
                className: cn("flex items-center gap-1.5 px-0 py-2.5"),
                children: /*#__PURE__*/ _jsx(Typography, {
                    variant: isMobile ? "subtitle1" : "h6",
                    className: cn("text-gray-500 transition-colors group-hover:text-gray-900", isOpen || isActive ? "text-gray-900" : ""),
                    children: title
                })
            }),
            isDropdown && isOpen && (isMobile || isTablet) && /*#__PURE__*/ _jsx("div", {
                className: "fixed inset-0 z-[899]",
                onClick: (e)=>{
                    e.stopPropagation();
                    onOpenChange(id);
                }
            }),
            isDropdown && isOpen && /*#__PURE__*/ _jsxs("div", {
                className: "fixed z-[900]",
                style: {
                    top: dropdownPosition.top,
                    left: dropdownPosition.left,
                    touchAction: "manipulation"
                },
                onPointerEnter: (e)=>!isMobile && !isTablet && e.pointerType === "mouse" && updatePosition(buttonRef.current),
                onPointerLeave: (e)=>!isMobile && !isTablet && e.pointerType === "mouse" && onOpenChange(id),
                onPointerUp: (e)=>e.stopPropagation(),
                children: [
                    !isMobile && !isTablet && /*#__PURE__*/ _jsx("div", {
                        className: "absolute -top-1.5 left-0 h-1.5 w-full"
                    }),
                    /*#__PURE__*/ _jsx("ul", {
                        className: "w-[120px] rounded-md border border-gray-100 bg-white py-2 shadow-sm",
                        children: items === null || items === void 0 ? void 0 : items.map((item, index)=>/*#__PURE__*/ _jsx("li", {
                                children: /*#__PURE__*/ _jsx("button", {
                                    type: "button",
                                    className: "w-full px-4 py-2 text-center hover:bg-gray-50",
                                    onClick: (e)=>{
                                        // click(탭의 마지막 단일 이벤트)에서 처리해야, 네비게이션·닫기로
                                        // 드롭다운/오버레이가 사라진 뒤 뒤따르는 ghost click이 뒤 배너로
                                        // 새서 잘못된 페이지로 이동하는 현상을 막을 수 있음
                                        e.stopPropagation();
                                        router.push(item.itemLink);
                                        onOpenChange(id);
                                    },
                                    children: /*#__PURE__*/ _jsx(Typography, {
                                        variant: "body2",
                                        className: "text-primary",
                                        children: item.itemLabel
                                    })
                                })
                            }, index))
                    })
                ]
            })
        ]
    });
};
